import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createLimitOrder } from "@/lib/api";

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ProtectProfitForm = ({ item, onDone }) => {
  const isShort = item.position_type === "short";
  const live = item.current_price || item.buy_price;
  const [mode, setMode] = useState("fixed");
  const [stop, setStop] = useState("");
  const [trail, setTrail] = useState("5");
  const [shares, setShares] = useState(String(item.shares));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStop(String(+((item.buy_price + live) / 2).toFixed(2))); }, [item.buy_price, live]);

  const n = parseFloat(shares);
  const pct = parseFloat(trail);
  const stopPx = mode === "fixed" ? parseFloat(stop) : +(live * (isShort ? 1 + pct / 100 : 1 - pct / 100)).toFixed(2);
  const wrongSide = isShort ? stopPx <= live : stopPx >= live;
  const valid = n > 0 && n <= item.shares && stopPx > 0 && !wrongSide && (mode === "fixed" || (pct >= 0.5 && pct <= 50));
  const locked = valid ? (isShort ? item.buy_price - stopPx : stopPx - item.buy_price) * n : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { portfolio_item_id: item.id, shares: n >= item.shares ? null : n };
      await createLimitOrder(mode === "fixed" ? { ...body, order_type: "stop_loss", target_price: stopPx } : { ...body, order_type: "trailing_stop", trail_pct: pct });
      toast.success(mode === "fixed" ? `Stop set: ${isShort ? "cover" : "sell"} ${item.symbol} if it hits $${fmt(stopPx)}` : `Trailing stop set: ${item.symbol} follows the price, ${pct}% cushion`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set stop");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="protect-profit-form">
      <div className="grid grid-cols-2 gap-2">
        {[["fixed", "Fixed price"], ["trailing", "Trailing %"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`protect-mode-${m}`}
            className={`py-2 text-xs font-mono border transition-colors ${mode === m ? "border-bt-gold bg-bt-gold text-bt-bg font-semibold" : "border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"}`}>{l}</button>
        ))}
      </div>
      <p className="text-[11px] text-bt-text-tertiary" data-testid="protect-hint">
        {mode === "fixed"
          ? `${isShort ? "Buys back" : "Sells"} automatically if the price ${isShort ? "rises" : "falls"} to your stop.`
          : `Follows the price as it ${isShort ? "falls" : "rises"} and ${isShort ? "covers" : "sells"} if it ${isShort ? "bounces up" : "drops"} this % from its best level.`}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-bt-text-secondary">{mode === "fixed" ? "Stop price" : "Trail by %"}</Label>
          {mode === "fixed"
            ? <Input type="number" min="0" step="any" value={stop} onChange={(e) => setStop(e.target.value)} className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="protect-stop-input" />
            : <Input type="number" min="0.5" max="50" step="0.5" value={trail} onChange={(e) => setTrail(e.target.value)} className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="protect-trail-input" />}
        </div>
        <div>
          <Label className="text-xs text-bt-text-secondary">Shares</Label>
          <Input type="number" min="0" max={item.shares} step="any" value={shares} onChange={(e) => setShares(e.target.value)} className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="protect-shares-input" />
        </div>
      </div>
      <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 font-mono text-xs space-y-1" data-testid="protect-preview">
        <p className="flex justify-between"><span className="text-bt-text-tertiary">Live price</span><span>${fmt(live)}</span></p>
        <p className="flex justify-between"><span className="text-bt-text-tertiary">{mode === "fixed" ? "Stop" : "Stop starts at"}</span><span data-testid="protect-stop-price">${fmt(stopPx)}</span></p>
        <p className="flex justify-between"><span className="text-bt-text-tertiary">{locked >= 0 ? "Profit locked in" : "Max loss"}</span>
          <span className={locked >= 0 ? "text-bt-positive" : "text-bt-negative"} data-testid="protect-locked">{locked >= 0 ? "+" : "-"}${fmt(Math.abs(locked))}</span></p>
      </div>
      {wrongSide && stopPx > 0 && (
        <p className="text-[11px] text-bt-negative" data-testid="protect-error">Stop must be {isShort ? "above" : "below"} the live price of ${fmt(live)}.</p>
      )}
      <Button onClick={submit} disabled={!valid || busy} className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light" data-testid="protect-submit-btn">
        {busy ? "Setting…" : mode === "fixed" ? "Set stop" : "Set trailing stop"}
      </Button>
    </div>
  );
};
